import React, { useState, useEffect } from 'react';
import ValueSelectorNew from './ValueSelector';
import { decodeValueReference, isNullOrEmpty } from '../../../utils/core';
import ValueSource from '../../../sources/value';
import { connect } from 'react-redux';
import { dataToValueOption } from './picker/valuePickerUtils';
import Loader from '../../Loader';
import classnames from 'classnames';

import '../../../../css/custom-component-picker.less';

/**
 * Component to wrap Value selector so that is reads and saves from and to value references
 * @param {String} id - id of this component
 * @param {String} flowKey - flowKey to identify the flow for manywho global to affect
 * @param {String} currentUser - details of the user currently logged in
 */
const ValueSelectorCustomComponent = ({ id, flowKey, currentUser }) => {

    const [isLoading, setIsLoading] = useState(true);
    const [isOpen, setIsOpen] = useState(false);
    const [error, setError] = useState(null);
    const [value, setValue] = useState(null);
    // value has shape
    // {
    //     contentType: "ContentNumber"
    //     developerName: "number dfdsf"
    //     elementMeta: "↵    ↵    ↵    ↵    ↵    Number↵    "
    //     elementName: "number dfdsf"
    //     id: "7a9a622e-c3f3-4411-90d3-58c13ec87ade"
    //     parentContentType: null
    //     propertyId: null
    // }

    const { isVisible, isValid, isEnabled, isRequired, label, contentValue, attributes, validationMessage } = manywho.model.getComponent(id, flowKey);

    if (isVisible === false) {
        return null;
    }

    // Receive value reference info
    const valueReferenceText = contentValue;

    const convertValueReference = () => {
        // Convert to value id from text
        // Take the first value and property names found
        const { valueName, propertyName } = decodeValueReference(valueReferenceText);
        // Do an api request for this named value
        return ValueSource.getList(currentUser.authenticationToken, currentUser.tenantId, { search: valueName })
            .then((response) => {
                // Find the exact value reference in the response
                const foundValue = response.find(valueReference => valueReference.developerName === valueName &&
                    valueReference.typeElementPropertyDeveloperName === propertyName);
                if (foundValue) {
                    setValue(dataToValueOption(foundValue));
                } else if (valueName) {
                    if (propertyName) {
                        setError(`Could not find value with name ${valueName} and property ${propertyName}`);
                    } else {
                        setError(`Could not find value with name ${valueName}`);
                    }
                } else {
                    setError(`Could not find value name within value reference ${valueReferenceText}`);
                }
                setIsLoading(false);
            }).catch((APIError) => {
                setError(APIError.message);
                setIsLoading(false);
            });
    };
    
    // Convert to value information from text
    // Will happen on mount and when valueReferenceText dependency changes
    useEffect(() => {
        if (valueReferenceText) {
            convertValueReference();
        } else {
            setValue(null);
            setIsLoading(false);
        }
    }, [
        valueReferenceText,
    ]);

    // Have a function to receive new selections
    const selectValue = (elementMeta) => {
        setValue(elementMeta);

        const { developerName, typeElementPropertyDeveloperName } = elementMeta;

        // Convert from value id to text
        const valueReference = isNullOrEmpty(typeElementPropertyDeveloperName) ?
            `{![${developerName}]}` :
            `{![${developerName}].[${typeElementPropertyDeveloperName}]}`;

        manywho.state.setComponent(
            id, { contentValue: valueReference }, flowKey, true,
        );

        setIsOpen(false);
    };

    return (
        isLoading ?
            <Loader /> :
            error ?
                <div>{error}</div> :
                (
                    <div className={classnames({
                        'form-group': true,
                        'custom-component-picker': true,
                        'has-error': isValid,
                        disabled: isEnabled,
                    })}
                    >
                        <label>
                            {label}
                            {isRequired ? <span className="input-required"> *</span> : null}
                        </label>
                        <ValueSelectorNew
                            id={null} // We don't care about multiple value picker instances
                            contentTypeFilter={[attributes.contentTypeFilter]} // Grab from system flow component the value contentType e.g. ContentString
                            placeholder="Select or create a Value" // Override the default placeholder displayed on the value select button
                            elementType="value" // What type of element this selector is selecting e.g. 'value' or 'type' default: 'value'
                            preSelectedValue={value} // Represents a selected value.
                            isOpen={isOpen} // Whether the picker modal is open or not
                            toggleModal={bool => setIsOpen(bool)} // Callback to specify whether the picker modal should be open or not
                            selectValue={selectValue} // Callback to set a value as selected
                            startingValueName={null} // Optional string to initialize the name of the new Value
                        />
                        {!isValid ? <span className="help-block">{validationMessage}</span> : null}
                    </div>
                )
    );
};

const mapStateToProps = state => ({
    currentUser: state.currentUser,
});

export default connect(mapStateToProps)(ValueSelectorCustomComponent);
